import { ArrowRight, Sparkles } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";

export const Hero = () => {
  const { t } = useLanguage();
  return (
    <section
      data-testid="hero-section"
      className="relative min-h-screen luxe-bg overflow-hidden pt-20 sm:pt-28 pb-12 sm:pb-16"
    >
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-12">
        <div className="flex flex-col items-center text-center fade-up pt-2 sm:pt-6 pb-10 sm:pb-14">
          <img
            src="/brand/smartxglass-logo.png"
            alt="smartxglass"
            data-testid="hero-logo"
            className="h-32 sm:h-48 md:h-64 lg:h-80 xl:h-96 w-auto logo-blink-x drop-shadow-[0_20px_60px_rgba(184,115,51,0.25)]"
          />
          <div className="flex items-center gap-2 sm:gap-3 mt-6 sm:mt-8 px-4">
            <div className="h-px w-8 sm:w-16 bg-[#b87333]" />
            <span className="text-[10px] sm:text-xs uppercase tracking-[0.22em] sm:tracking-[0.32em] text-[#d89557] text-center">
              {t("hero.tagline")}
            </span>
            <div className="h-px w-8 sm:w-16 bg-[#b87333]" />
          </div>
        </div>

        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7 fade-up delay-1">
            <h1 className="font-display text-[clamp(2.5rem,8vw,6rem)] leading-[0.95] text-[#f4ede0] mb-6 sm:mb-8 text-center lg:text-left">
              {t("hero.title1")} <br />
              <span className="copper-text italic">{t("hero.title2")}</span> {t("hero.title3")}
              <br />
              <span className="italic font-light text-[#cbc1ad]">{t("hero.title4")}</span>
            </h1>

            <p className="text-sm sm:text-base lg:text-lg text-[#cbc1ad] max-w-xl mb-8 sm:mb-10 leading-relaxed text-center lg:text-left mx-auto lg:mx-0">
              {(() => {
                const parts = t("hero.desc").split("{brand}");
                return (
                  <>
                    {parts[0]}
                    <span className="text-[#d89557]">SMART-FILM</span>
                    {parts[1] || ""}
                  </>
                );
              })()}
            </p>

            <div className="flex flex-wrap gap-3 sm:gap-4 justify-center lg:justify-start fade-up delay-2">
              <a
                data-testid="hero-cta-configure"
                href="#configurateur"
                className="btn-copper px-8 py-4 text-sm uppercase tracking-[0.2em] inline-flex items-center gap-3"
              >
                {t("hero.ctaConfigure")}
                <ArrowRight className="w-4 h-4" />
              </a>
              <a
                data-testid="hero-cta-contact"
                href="/contact"
                className="btn-ghost-copper px-8 py-4 text-sm uppercase tracking-[0.2em] inline-flex items-center gap-3"
              >
                {t("hero.ctaContact")}
              </a>
            </div>

            <div className="mt-10 sm:mt-14 grid grid-cols-3 gap-4 sm:gap-8 max-w-lg mx-auto lg:mx-0 fade-up delay-3 text-center lg:text-left">
              <Stat label={t("hero.stats.reaction")} value="< 20ms" />
              <Stat label={t("hero.stats.light")} value="> 82%" />
              <Stat label={t("hero.stats.lifespan")} value="50 000h" />
            </div>
          </div>

          <div className="lg:col-span-5 relative fade-up delay-2">
            <div className="relative aspect-[4/5] rounded-sm overflow-hidden lcd-frame bg-[#0a0a0a]">
              <div
                className="absolute inset-0 bg-cover bg-center opacity-60"
                style={{
                  backgroundImage:
                    "url('https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&w=1200&q=80')",
                }}
              />
              <video
                data-testid="hero-demo-video"
                src="/media/demo.mp4"
                autoPlay
                muted
                loop
                playsInline
                preload="auto"
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-transparent to-transparent pointer-events-none" />
              <div className="absolute top-6 right-6 px-3 py-1 border border-[#b87333]/50 bg-[#0a0a0a]/70 backdrop-blur text-[10px] uppercase tracking-[0.3em] text-[#d89557]">
                {t("hero.badgeOff")}
              </div>
            </div>

            <div className="absolute -bottom-6 -left-6 glass-card px-5 py-4 max-w-[240px]">
              <div className="flex items-center gap-2 mb-1">
                <Sparkles className="w-3.5 h-3.5 text-[#d89557]" />
                <span className="text-[10px] uppercase tracking-[0.25em] text-[#d89557]">
                  {t("hero.badgeCorner")}
                </span>
              </div>
              <p className="text-xs text-[#cbc1ad] leading-relaxed">{t("hero.cornerDesc")}</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const Stat = ({ label, value }) => (
  <div>
    <div className="font-display text-2xl sm:text-3xl text-[#f4ede0]">{value}</div>
    <div className="text-[9px] sm:text-[10px] uppercase tracking-[0.22em] sm:tracking-[0.25em] text-[#7a716a] mt-1">
      {label}
    </div>
  </div>
);
